import { useEffect, useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import {
  Button,
  Card,
  DatePicker,
  Descriptions,
  Drawer,
  Input,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons'
import type { Dayjs } from 'dayjs'
import { supabase } from '@/lib/supabase'
import { formatMembershipDuration, snapshotIsPermanent } from '@/lib/products'
import { formatCST, pickerToUTCISO } from '@/lib/time'
import { AdminFilterBar } from '@/components/AdminFilterBar'
import { AdminMobileList } from '@/components/AdminMobileList'
import { useIsMobile } from '@/lib/hooks'

export const Route = createFileRoute('/_authed/orders')({
  component: OrdersPage,
})

/* ── 类型 ───────────────────────────────────────────────────── */

type OrderStatus = 'pending' | 'paid' | 'expired' | 'cancelled' | 'refunded'
type StatusFilter = OrderStatus | 'all'
type LegacyFilter = 'all' | 'only' | 'exclude'
type SortKey = 'created_desc' | 'paid_desc' | 'amount_desc'

interface OrderRow {
  id: string
  trade_no: string
  amount: number | string
  status: OrderStatus
  created_at: string
  paid_at: string | null
  pay_method: string | null
  product_id: string | null
  product_name: string | null
  user_id: string | null
  legacy_user_id: number | null
  user_email: string | null
  user_username: string | null
  user_display: string | null
  is_legacy: boolean
  product_snapshot: Record<string, unknown> | null
}

interface ListResult {
  ok: boolean
  total?: number
  amount_sum?: number | string
  rows?: Array<OrderRow>
  error?: string
}

interface ProductOption {
  id: string
  name: string
}

/* ── i18n ───────────────────────────────────────────────────── */

const STATUS_LABELS: Record<OrderStatus, string> = {
  pending: '待支付',
  paid: '已支付',
  expired: '订单过期',
  cancelled: '已取消',
  refunded: '已退款',
}

const PAY_METHOD_LABELS: Partial<Record<string, string>> = {
  alipay: '支付宝',
  wxpay: '微信支付',
  hupay: 'Hupay',
  wechat: '微信支付',
}

const SORT_LABELS: Record<SortKey, string> = {
  created_desc: '创建时间倒序',
  paid_desc: '支付时间倒序',
  amount_desc: '金额倒序',
}

function statusColor(s: OrderStatus) {
  switch (s) {
    case 'paid':
      return 'success'
    case 'pending':
      return 'warning'
    case 'expired':
      return 'default'
    case 'cancelled':
      return 'default'
    case 'refunded':
      return 'error'
    default:
      return 'default'
  }
}

/* ── helpers ────────────────────────────────────────────────── */

const PAGE_SIZE = 50

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return v
}

function asNumber(v: number | string | null | undefined): number {
  if (v === null || v === undefined) return 0
  return typeof v === 'string' ? Number(v) : v
}

function formatAmount(n: number): string {
  return n.toLocaleString('zh-CN', {
    minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
    maximumFractionDigits: 2,
  })
}

function formatOrderDuration(snapshot: Record<string, unknown> | null): string {
  return formatMembershipDuration({
    isPermanent: snapshotIsPermanent(snapshot),
    durationDays:
      typeof snapshot?.duration_days === 'number' ||
      typeof snapshot?.duration_days === 'string'
        ? snapshot.duration_days
        : null,
  })
}

/* ── 主组件 ─────────────────────────────────────────────────── */

function OrdersPage() {
  const [status, setStatus] = useState<StatusFilter>('all')
  const [legacyFilter, setLegacyFilter] = useState<LegacyFilter>('all')
  const [productId, setProductId] = useState<string | undefined>()
  const [sort, setSort] = useState<SortKey>('created_desc')
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounced(search.trim(), 300)
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null)
  const [page, setPage] = useState(1)
  const [detail, setDetail] = useState<OrderRow | null>(null)
  const isMobile = useIsMobile()

  useEffect(() => {
    setPage(1)
  }, [
    debouncedSearch,
    status,
    legacyFilter,
    productId,
    sort,
    range?.[0]?.valueOf(),
    range?.[1]?.valueOf(),
  ])

  // 商品下拉（公开可读）
  const productsQuery = useQuery({
    queryKey: ['products_for_orders_filter'],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('products')
        .select('id, name')
        .eq('type', 'membership')
        .order('sort_order', { ascending: true })
      if (error) throw error
      return (data ?? []) as Array<ProductOption>
    },
    staleTime: 5 * 60_000,
  })

  const listQuery = useQuery({
    queryKey: [
      'admin_orders',
      {
        page,
        status,
        legacyFilter,
        productId,
        sort,
        search: debouncedSearch,
        from: pickerToUTCISO(range?.[0]),
        to: pickerToUTCISO(range?.[1]),
      },
    ],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('list_orders', {
          p_search: debouncedSearch || null,
          p_status: status === 'all' ? null : status,
          p_legacy: legacyFilter === 'all' ? null : legacyFilter,
          p_product_id: productId ?? null,
          p_from: pickerToUTCISO(range?.[0]),
          p_to: pickerToUTCISO(range?.[1]),
          p_sort: sort,
          p_limit: PAGE_SIZE,
          p_offset: (page - 1) * PAGE_SIZE,
        })
      if (error) throw error
      const r = data as ListResult
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return {
        rows: r.rows ?? [],
        total: r.total ?? 0,
        amountSum: asNumber(r.amount_sum),
      }
    },
  })

  const columns: ColumnsType<OrderRow> = useMemo(
    () => [
      {
        title: '订单号',
        dataIndex: 'trade_no',
        width: 200,
        render: (s: string, row) => (
          <Space direction="vertical" size={0}>
            <Typography.Text
              copyable={{ text: s }}
              style={{
                fontSize: 12,
                fontFamily: 'var(--ant-font-family-code, monospace)',
              }}
            >
              {s}
            </Typography.Text>
            {row.is_legacy ? (
              <Tag style={{ marginRight: 0, fontSize: 10 }}>历史导入</Tag>
            ) : null}
          </Space>
        ),
      },
      {
        title: '用户',
        width: 220,
        render: (_: unknown, row) => {
          if (row.user_id) {
            return (
              <Space direction="vertical" size={0}>
                <Typography.Text style={{ fontSize: 13 }}>
                  {row.user_display ?? '—'}
                </Typography.Text>
                {row.user_username ? (
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    @{row.user_username}
                  </Typography.Text>
                ) : null}
                {row.user_email ? (
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: 11 }}
                    ellipsis
                  >
                    {row.user_email}
                  </Typography.Text>
                ) : null}
              </Space>
            )
          }
          // 未认领
          return (
            <Space direction="vertical" size={0}>
              <Tag color="default" style={{ marginRight: 0 }}>
                未认领
              </Tag>
              <Typography.Text
                type="secondary"
                style={{
                  fontSize: 11,
                  fontFamily: 'var(--ant-font-family-code, monospace)',
                }}
              >
                legacy uid {row.legacy_user_id ?? '—'}
              </Typography.Text>
            </Space>
          )
        },
      },
      {
        title: '商品',
        width: 140,
        render: (_: unknown, row) => (
          <Space direction="vertical" size={0}>
            <Typography.Text style={{ fontSize: 13 }}>
              {row.product_name ?? '—'}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              {formatOrderDuration(row.product_snapshot)}
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: '金额',
        width: 100,
        align: 'right',
        render: (_: unknown, row) => (
          <Typography.Text
            style={{
              fontSize: 13,
              fontWeight: 500,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            ¥{formatAmount(asNumber(row.amount))}
          </Typography.Text>
        ),
      },
      {
        title: '状态',
        dataIndex: 'status',
        width: 90,
        render: (s: OrderStatus) => (
          <Tag color={statusColor(s)}>{STATUS_LABELS[s]}</Tag>
        ),
      },
      {
        title: '支付',
        dataIndex: 'pay_method',
        width: 100,
        render: (m: string | null) =>
          m ? (
            <Typography.Text style={{ fontSize: 12 }}>
              {PAY_METHOD_LABELS[m] ?? m}
            </Typography.Text>
          ) : (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              —
            </Typography.Text>
          ),
      },
      {
        title: '时间',
        width: 170,
        render: (_: unknown, row) => (
          <Space direction="vertical" size={0}>
            <Tooltip title="创建时间" mouseEnterDelay={0.4}>
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                创 {formatCST(row.created_at, 'YYYY-MM-DD HH:mm')}
              </Typography.Text>
            </Tooltip>
            {row.paid_at ? (
              <Tooltip title="支付时间" mouseEnterDelay={0.4}>
                <Typography.Text style={{ fontSize: 12 }}>
                  付 {formatCST(row.paid_at, 'MM-DD HH:mm')}
                </Typography.Text>
              </Tooltip>
            ) : null}
          </Space>
        ),
      },
      {
        title: '操作',
        width: 70,
        fixed: 'right',
        render: (_: unknown, row) => (
          <Button
            type="link"
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              setDetail(row)
            }}
          >
            详情
          </Button>
        ),
      },
    ],
    [],
  )

  const rows = listQuery.data?.rows ?? []
  const total = listQuery.data?.total ?? 0
  const amountSum = listQuery.data?.amountSum ?? 0

  return (
    <>
      <Card
        title="订单"
        extra={
          <Tooltip title={isMobile ? '刷新' : undefined}>
            <Button
              icon={<ReloadOutlined />}
              aria-label="刷新"
              onClick={() => listQuery.refetch()}
              loading={listQuery.isFetching && !listQuery.isLoading}
            >
              {isMobile ? null : '刷新'}
            </Button>
          </Tooltip>
        }
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {/* 6 个状态在手机上放进横向滚动轨道，避免挤压换行 */}
          <div className={isMobile ? 'admin-scroll-rail' : undefined}>
            <Segmented<StatusFilter>
              value={status}
              onChange={(v) => setStatus(v)}
              options={[
                { label: '全部', value: 'all' },
                { label: '已支付', value: 'paid' },
                { label: '待支付', value: 'pending' },
                { label: '已过期', value: 'expired' },
                { label: '已取消', value: 'cancelled' },
                { label: '已退款', value: 'refunded' },
              ]}
            />
          </div>

          <AdminFilterBar
            activeCount={
              (legacyFilter !== 'all' ? 1 : 0) +
              (productId ? 1 : 0) +
              (range ? 1 : 0)
            }
            primary={
              <Input
                allowClear
                prefix={<SearchOutlined />}
                placeholder="订单号 / 邮箱 / 用户名 / 昵称"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ width: 280 }}
              />
            }
          >
            <Select
              allowClear
              placeholder="是否历史导入"
              style={{ width: 160 }}
              value={legacyFilter === 'all' ? undefined : legacyFilter}
              onClear={() => setLegacyFilter('all')}
              onChange={(v) => setLegacyFilter(v)}
              options={[
                { value: 'only', label: '仅历史导入' },
                { value: 'exclude', label: '仅新订单' },
              ]}
            />
            <Select
              allowClear
              placeholder="商品"
              style={{ width: 160 }}
              value={productId}
              onClear={() => setProductId(undefined)}
              onChange={(v) => setProductId(v)}
              options={(productsQuery.data ?? []).map((p) => ({
                value: p.id,
                label: p.name,
              }))}
              loading={productsQuery.isLoading}
            />
            <Select<SortKey>
              value={sort}
              onChange={setSort}
              style={{ width: 180 }}
              options={Object.entries(SORT_LABELS).map(([value, label]) => ({
                value: value as SortKey,
                label,
              }))}
            />
            <DatePicker.RangePicker
              showTime={{ format: 'HH:mm' }}
              format="YYYY-MM-DD HH:mm"
              value={range}
              onChange={(r) =>
                setRange(r && r[0] && r[1] ? [r[0], r[1]] : null)
              }
            />
          </AdminFilterBar>

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px 24px',
              alignItems: 'baseline',
              padding: '8px 12px',
              background: 'var(--ant-color-fill-quaternary)',
              borderRadius: 6,
            }}
          >
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
              筛选结果
            </Typography.Text>
            <Typography.Text style={{ fontSize: 13 }}>
              共{' '}
              <Typography.Text
                strong
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {total.toLocaleString('zh-CN')}
              </Typography.Text>{' '}
              笔
            </Typography.Text>
            <Typography.Text style={{ fontSize: 13 }}>
              合计{' '}
              <Typography.Text
                strong
                style={{
                  fontVariantNumeric: 'tabular-nums',
                  fontSize: 15,
                }}
              >
                ¥{formatAmount(amountSum)}
              </Typography.Text>
            </Typography.Text>
          </div>

          <div className="admin-table-wrap">
            <Table<OrderRow>
              rowKey="id"
              size="middle"
              loading={listQuery.isLoading}
              columns={columns}
              dataSource={rows}
              onRow={(row) => ({
                onClick: () => setDetail(row),
                style: { cursor: 'pointer' },
              })}
              scroll={{ x: 1200 }}
              pagination={{
                current: page,
                pageSize: PAGE_SIZE,
                total,
                showSizeChanger: false,
                showTotal: (totalRows) => `共 ${totalRows} 条`,
                onChange: (p) => setPage(p),
              }}
            />
          </div>

          <AdminMobileList
            rows={rows}
            rowKey={(row) => row.id}
            loading={listQuery.isLoading || listQuery.isFetching}
            page={page}
            pageSize={PAGE_SIZE}
            total={total}
            onPageChange={setPage}
            renderItem={(row) => (
              <article
                className="admin-mobile-record"
                role="button"
                tabIndex={0}
                onClick={() => setDetail(row)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') setDetail(row)
                }}
              >
                <div className="admin-mobile-record__head">
                  <div className="admin-mobile-record__title">
                    <Typography.Text copyable={{ text: row.trade_no }}>
                      {row.trade_no}
                    </Typography.Text>
                  </div>
                  <Tag color={statusColor(row.status)}>
                    {STATUS_LABELS[row.status]}
                  </Tag>
                </div>
                <div className="admin-mobile-record__badges">
                  {row.is_legacy ? <Tag>历史导入</Tag> : null}
                  {!row.user_id ? <Tag color="default">未认领</Tag> : null}
                  {row.pay_method ? (
                    <Tag>
                      {PAY_METHOD_LABELS[row.pay_method] ?? row.pay_method}
                    </Tag>
                  ) : null}
                </div>
                <div className="admin-mobile-record__body">
                  {row.product_name ?? '未知商品'} ·{' '}
                  {formatOrderDuration(row.product_snapshot)} · ¥
                  {formatAmount(asNumber(row.amount))}
                </div>
                <div className="admin-mobile-record__meta">
                  <span>
                    用户{' '}
                    {row.user_display ??
                      row.user_username ??
                      row.user_email ??
                      `legacy ${row.legacy_user_id ?? '—'}`}
                  </span>
                  <span>创建 {formatCST(row.created_at, 'MM-DD HH:mm')}</span>
                  {row.paid_at ? (
                    <span>支付 {formatCST(row.paid_at, 'MM-DD HH:mm')}</span>
                  ) : null}
                </div>
              </article>
            )}
          />
        </Space>
      </Card>

      <DetailDrawer row={detail} onClose={() => setDetail(null)} />
    </>
  )
}

/* ── 详情抽屉 ───────────────────────────────────────────────── */

function DetailDrawer({
  row,
  onClose,
}: {
  row: OrderRow | null
  onClose: () => void
}) {
  const isMobile = useIsMobile()
  return (
    <Drawer
      open={!!row}
      onClose={onClose}
      width={isMobile ? '100%' : 640}
      title={row ? `订单 · ${row.trade_no}` : ''}
      destroyOnHidden
    >
      {row ? (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Descriptions
            column={1}
            size="small"
            bordered
            className="orders-detail-desc"
            items={[
              {
                label: '订单号',
                children: (
                  <Typography.Text
                    copyable
                    style={{
                      fontFamily: 'var(--ant-font-family-code, monospace)',
                    }}
                  >
                    {row.trade_no}
                  </Typography.Text>
                ),
              },
              {
                label: '订单 ID',
                children: (
                  <Typography.Text
                    type="secondary"
                    copyable
                    style={{
                      fontSize: 12,
                      fontFamily: 'var(--ant-font-family-code, monospace)',
                    }}
                  >
                    {row.id}
                  </Typography.Text>
                ),
              },
              {
                label: '状态',
                children: (
                  <Tag color={statusColor(row.status)}>
                    {STATUS_LABELS[row.status]}
                  </Tag>
                ),
              },
              {
                label: '商品',
                children: row.product_name ?? '—',
              },
              {
                label: '时长',
                children: formatOrderDuration(row.product_snapshot),
              },
              {
                label: '金额',
                children: (
                  <Typography.Text
                    style={{
                      fontSize: 16,
                      fontWeight: 500,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    ¥{formatAmount(asNumber(row.amount))}
                  </Typography.Text>
                ),
              },
              {
                label: '支付方式',
                children: row.pay_method
                  ? (PAY_METHOD_LABELS[row.pay_method] ?? row.pay_method)
                  : '—',
              },
              {
                label: '用户',
                children: row.user_id ? (
                  <Space direction="vertical" size={2}>
                    <span>{row.user_display ?? '—'}</span>
                    {row.user_username ? (
                      <Typography.Text type="secondary">
                        @{row.user_username}
                      </Typography.Text>
                    ) : null}
                    {row.user_email ? (
                      <Typography.Text type="secondary" copyable>
                        {row.user_email}
                      </Typography.Text>
                    ) : null}
                    <Typography.Text
                      type="secondary"
                      copyable
                      style={{
                        fontSize: 12,
                        fontFamily: 'var(--ant-font-family-code, monospace)',
                      }}
                    >
                      {row.user_id}
                    </Typography.Text>
                  </Space>
                ) : (
                  <Space direction="vertical" size={2}>
                    <Tag>未认领</Tag>
                    <Typography.Text type="secondary">
                      legacy uid {row.legacy_user_id ?? '—'}
                    </Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      用户首次走 legacy 登录后会自动认领
                    </Typography.Text>
                  </Space>
                ),
              },
              {
                label: '创建时间',
                children: formatCST(row.created_at),
              },
              {
                label: '支付时间',
                children: formatCST(row.paid_at),
              },
              {
                label: '商品快照',
                children: (
                  <pre
                    style={{
                      margin: 0,
                      padding: 12,
                      background: 'var(--ant-color-fill-tertiary)',
                      borderRadius: 4,
                      fontSize: 12,
                      maxHeight: 320,
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {JSON.stringify(row.product_snapshot ?? {}, null, 2)}
                  </pre>
                ),
              },
            ]}
          />
        </Space>
      ) : null}
    </Drawer>
  )
}
